import { InjectionToken } from '@angular/core';

/**
 * Responsive layout breakpoint: `alias` for classnames and template logic, `mediaQuery` for
 * `matchMedia` / CDK `BreakpointObserver`. `overlapping` flags ranges that are not discrete bands
 * (same idea as the old flex-layout grid).
 */
export interface IBreakpoint {
  alias: string;
  mediaQuery: string;
  overlapping?: boolean;
}

/**
 * Injected in each app module so `BreakpointsService` uses the same aliases and media queries as
 * the host layout (client vs member use different `md` / `lt-lg` edges).
 * Use {@link mergeFlexAndCustomSonderBreakpoints} with the same grid array the app uses for layout.
 */
export const SONDER_BREAKPOINTS = new InjectionToken<readonly IBreakpoint[]>(
  'Layout breakpoints for BreakpointsService'
);

/**
 * Client-portal default grid: same shape as the historical flex-layout set, with the upper end of
 * `md` at 1440 (not 1280). Use with e.g. `FlexLayoutModule.withConfig` until flex-layout is removed.
 */
export const CLIENT_FLEX_DEFAULT_BREAKPOINTS: IBreakpoint[] = [
  { alias: 'xs', mediaQuery: '(min-width: 0px) and (max-width: 599px)' },
  { alias: 'gt-xs', overlapping: true, mediaQuery: '(min-width: 600px)' },
  { alias: 'lt-sm', overlapping: true, mediaQuery: '(max-width: 599px)' },
  { alias: 'sm', mediaQuery: '(min-width: 600px) and (max-width: 959px)' },
  { alias: 'gt-sm', overlapping: true, mediaQuery: '(min-width: 960px)' },
  { alias: 'lt-md', overlapping: true, mediaQuery: '(max-width: 959px)' },
  { alias: 'md', mediaQuery: '(min-width: 960px) and (max-width: 1439px)' },
  { alias: 'gt-md', overlapping: true, mediaQuery: '(min-width: 1440px)' },
  { alias: 'lt-lg', overlapping: true, mediaQuery: '(max-width: 1439px)' },
  { alias: 'lg', mediaQuery: '(min-width: 1440px) and (max-width: 1919px)' },
  { alias: 'gt-lg', overlapping: true, mediaQuery: '(min-width: 1920px)' },
  { alias: 'lt-xl', overlapping: true, mediaQuery: '(max-width: 1919px)' },
  { alias: 'xl', mediaQuery: '(min-width: 1920px) and (max-width: 5000px)' }
];

/**
 * Member-portal default grid (differs from client and has lower width boundaries).
 */
export const MEMBER_FLEX_DEFAULT_BREAKPOINTS: IBreakpoint[] = [
  { alias: 'xs', mediaQuery: '(min-width: 0px) and (max-width: 475px)' },
  { alias: 'gt-xs', overlapping: true, mediaQuery: '(min-width: 476px)' },
  { alias: 'lt-sm', overlapping: true, mediaQuery: '(max-width: 475px)' },
  { alias: 'sm', mediaQuery: '(min-width: 476px) and (max-width: 767px)' },
  { alias: 'gt-sm', overlapping: true, mediaQuery: '(min-width: 768px)' },
  { alias: 'lt-md', overlapping: true, mediaQuery: '(max-width: 767px)' },
  { alias: 'md', mediaQuery: '(min-width: 768px) and (max-width: 1024px)' },
  { alias: 'gt-md', overlapping: true, mediaQuery: '(min-width: 1025px)' },
  { alias: 'lt-lg', overlapping: true, mediaQuery: '(max-width: 1024px)' },
  { alias: 'lg', mediaQuery: '(min-width: 1025px) and (max-width: 1919px)' },
  { alias: 'gt-lg', overlapping: true, mediaQuery: '(min-width: 1920px)' },
  { alias: 'lt-xl', overlapping: true, mediaQuery: '(max-width: 1919px)' },
  { alias: 'xl', mediaQuery: '(min-width: 1920px) and (max-width: 5000px)' }
];

/**
 * Provider default grid: same as client grid but with the upper end of `md` at 1280 (not 1440).
 * Used for provider landing page and provider directory projects.
 */
export const PUBLIC_FLEX_DEFAULT_BREAKPOINTS: IBreakpoint[] = [
  { alias: 'xs', mediaQuery: '(min-width: 0px) and (max-width: 599px)' },
  { alias: 'gt-xs', overlapping: true, mediaQuery: '(min-width: 600px)' },
  { alias: 'lt-sm', overlapping: true, mediaQuery: '(max-width: 599px)' },
  { alias: 'sm', mediaQuery: '(min-width: 600px) and (max-width: 959px)' },
  { alias: 'gt-sm', overlapping: true, mediaQuery: '(min-width: 960px)' },
  { alias: 'lt-md', overlapping: true, mediaQuery: '(max-width: 959px)' },
  { alias: 'md', mediaQuery: '(min-width: 960px) and (max-width: 1279px)' },
  { alias: 'gt-md', overlapping: true, mediaQuery: '(min-width: 1280px)' },
  { alias: 'lt-lg', overlapping: true, mediaQuery: '(max-width: 1279px)' },
  { alias: 'lg', mediaQuery: '(min-width: 1280px) and (max-width: 1919px)' },
  { alias: 'gt-lg', overlapping: true, mediaQuery: '(min-width: 1920px)' },
  { alias: 'lt-xl', overlapping: true, mediaQuery: '(max-width: 1919px)' },
  { alias: 'xl', mediaQuery: '(min-width: 1920px) and (max-width: 5000px)' }
];

const SONDER_CUSTOM_BREAKPOINTS: IBreakpoint[] = [
  { alias: 'top-nav-breakpoint', mediaQuery: '(max-width: 1002px)', overlapping: false },
  { alias: 'match-builder-mobile-breakpoint', mediaQuery: '(max-width: 641px)', overlapping: false },
  { alias: 'match-builder-single-column-breakpoint', mediaQuery: '(max-width: 898px)', overlapping: false }
];

/**
 * Appends shared product-specific queries to the app layout grid. Pass the same `grid` your app
 * uses for `FlexLayoutModule.withConfig` (or a future non-flex layout) and register the return
 * value with {@link SONDER_BREAKPOINTS}.
 */
export function mergeFlexAndCustomSonderBreakpoints(
  grid: readonly IBreakpoint[]
): readonly IBreakpoint[] {
  return [
    ...grid,
    ...SONDER_CUSTOM_BREAKPOINTS
  ];
}

/** @deprecated Use `CLIENT_FLEX_DEFAULT_BREAKPOINTS` */
export const DEFAULT_FLEX_BREAKPOINTS = CLIENT_FLEX_DEFAULT_BREAKPOINTS;
